import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Typography, Button, Spin, Dropdown, Menu } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useDisclosure, useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString } from "@/shared/utils";
import { useTitleAmendmentMode } from "../../../../model";
import { RepresentativeWrapper } from "../../../representative-wrapper";
import { RepresentativesSignatureWrapper } from "../../../representatives-signature-wrapper";

export function DeveloperControls() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [key, setKey] = useState(0);
  const { titleId } = useCoordinator();
  const { data: titleData, isFetching: isTitleFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const developer = titleData?.mainJournal?.titleJournal?.developer;

  const titleStatus = titleData?.mainJournal?.titleJournal?.status === "NEW";
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const controls =
    titleData?.mainJournal?.titleJournal?.developer?.constructionControls || [];
  const hasConstructionControls =
    developer?.projectParticipantId && controls.length > 0;
  const handleClose = () => {
    onClose();
    setKey((prev) => prev + 1);
  };

  const { mutate: removeParticipant, isPending } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/constructionControl",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleDelete = (id: string) => {
    removeParticipant({
      params: {
        path: {
          titleId,
        },
      },
      body: {
        participants: {
          developer: {
            projectParticipantId: developer?.projectParticipantId,
            constructionControls: [{ constructionControlId: id }],
          },
        },
      },
    });
  };

  const menu = (id: string) => (
    <Menu>
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={() => handleDelete(id)}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  return (
    <Spin spinning={isTitleFetching || isPending}>
      <div>
        {canAddData ? (
          <div className="journal-title-block-header journal-title-block-header--spaced">
            <div className="journal-title-section-heading">
              Уполномоченный застройщика по вопросам стройконтроля
            </div>

            <Can I="edit" a="title_journals">
              <Button
                type="primary"
                onClick={onOpen}
                disabled={!developer?.projectParticipantId || isPending}
              >
                Добавить сотрудника
              </Button>
            </Can>
            <RepresentativeModalOrgs
              key={key}
              organization={{
                representativeType: "developer",
                isConstructionControl: true,
                companyId: developer?.company?.companyId ?? "",
                projectParticipantId: developer?.projectParticipantId ?? "",
                userCompaniesHistoryId:
                  developer?.company?.companyHistoryId ?? "",
                // @ts-expect-error developerType отсутствует в openapi типе
                developerType: developer?.developerType ?? "developer",
              }}
              isOpen={isOpen}
              handleClose={handleClose}
              isControls
              existingRepresentatives={controls}
              enableMultiAdd
              isAddFromOldService
            />
          </div>
        ) : null}
        {hasConstructionControls
          ? controls.map((control, index) => (
              <RepresentativesSignatureWrapper
                representative={control}
                representativeType="developer"
                developerType={developer?.developerType!}
                isConstructionControl
              >
                <div
                  key={control.constructionControlId || index}
                  className="journal-title-rep-card"
                >
                  <div className="journal-title-rep-card__header">
                    <Typography.Title
                      level={5}
                      className="journal-title-rep-card__name"
                    >
                      {fallbackString(
                        `${control.user?.surname || ""} ${control.user?.firstName || ""} ${control.user?.middleName || ""}`,
                      )}
                    </Typography.Title>
                    {control.constructionControlId &&
                      titleStatus &&
                      !isNewAmendment && (
                        <Can I="edit" a="title_journals">
                          <Dropdown
                            overlay={menu(control?.constructionControlId)}
                            trigger={["click"]}
                          >
                            <div className="journal-title-menu-trigger">
                              <MoreOneIcon width={24} height={24} />
                            </div>
                          </Dropdown>
                        </Can>
                      )}
                  </div>

                  <RepresentativeWrapper rep={control} />
                </div>
              </RepresentativesSignatureWrapper>
            ))
          : null}
      </div>
    </Spin>
  );
}
